export interface RaspiPropertiesConfig {
  /** `portName`: where the auto-detected basic devices are, independent of `legacySerialPortConfig`. */
  basicDevicesSerialPort: SerialPort
  /**
   * References to named Modbus clients from device families that are not modelled here
   * (`controller.metering.*`, `controller.steering.*`, `controller.nwBattery`). Read-only:
   * they exist so that a connection still in use elsewhere is not removed from here.
   */
  externalModbusClientReferences: ExternalModbusClientReference[]
  legacyModbusConnections: LegacyModbusConnection[]
  legacyModbusMeters: LegacyModbusMetersConfig
  legacyModbusSerialConfig: LegacyModbusSerialConfig
  legacySerialPortConfig: LegacySerialPortConfig
  tillquistConfig: TillquistConfig
  /** Declared clients with no deducible transport. Not modelled, but their names are still taken. */
  unrecognisedModbusClientNames: string[]
}

export interface LegacyModbusConnection {
  id: string
  errors: string[]
  meters: LegacyModbusMeter[]
}

export interface LegacyModbusSerialConnection extends LegacyModbusConnection {
  serialPort: SerialPort
}

export enum SerialPort {
  port1 = 'port1',
  port2 = 'port2',
}

export interface LegacyModbusTcpConnection extends LegacyModbusConnection {
  tcpTarget: ModbusTcpHostConfig
}

export interface ModbusTcpHostConfig {
  host: string
  port: number
}

/**
 * A connection declared once under `controller.modbus.clients.<name>` and referenced by name from
 * the devices that use it. Unlike the legacy inline endpoint, it can carry connection-level
 * timeouts and retries, which the controller reads only from here.
 */
export interface NamedModbusConnection extends LegacyModbusConnection {
  namedClient: NamedModbusClient
}

export type NamedModbusClient = NamedModbusTcpClient | NamedModbusSerialClient

/**
 * The controller deduces the transport from which keys are present, checking `baudRate` before
 * `address`. Modelled explicitly here so the same order is preserved on read and write.
 */
export enum NamedModbusTransport {
  serial = 'serial',
  tcp = 'tcp',
}

interface NamedModbusClientBase {
  name: string
  /** Blank means the controller default; see NAMED_MODBUS_CLIENT_DEFAULTS. */
  responseTimeoutMs?: number
  retries?: number
}

export interface NamedModbusTcpClient extends NamedModbusClientBase {
  address: string
  connectTimeoutMs?: number
  port: number
  transport: NamedModbusTransport.tcp
}

/**
 * The RTU variant has no connect timeout — a serial line has no handshake — and no encoding or
 * transmission delay, neither of which `ModbusClientRtuConfiguration` carries.
 */
export interface NamedModbusSerialClient extends NamedModbusClientBase {
  /** Serialized as a single `mode` token, e.g. `8E1`, alongside `baudRate`. */
  portConfig: SerialPortConfig
  /** Written as a bare device name, e.g. `ttyS7`. */
  serialPort: SerialPort
  transport: NamedModbusTransport.serial
}

export interface ExternalModbusClientReference {
  clientName: string
  /** The id within the family, absent for a singleton family such as `controller.nwBattery`. */
  deviceId?: string
  /** `metering`, `steering`, `nwBattery`; kept open so an unfamiliar family is still shown. */
  family: string
  /** The full property key holding the reference, shown to explain why a connection is in use. */
  propertyKey: string
}

export interface LegacyModbusMeter {
  activePowerRegisters?: ModbusActivePowerRegisters
  activePowerScale: number
  dataTimestampRegister?: ModbusRegister
  deviceNumber: number
  errors: string[]
  meterErrorRegister?: ModbusRegister
  number: number
  powerMeasurementSkipPeriodInMilliseconds: number
  /** Applies both to active power and reactive power registers */
  powerRegistersDataType: ModbusDataType
  reactivePowerRegister?: ModbusRegister
  reactivePowerScale: number
  registerAddressOffset: number
  systemTimestampRegister?: ModbusRegister
  unitId: number
  watchdogRegister?: ModbusRegister
  wordOrder: WordOrder
}

export interface ModbusActivePowerRegisters extends ModbusRegister {
  variant: ModbusPowerRegisterVariant
}

export enum ModbusPowerRegisterVariant {
  threePhase = 'threePhase',
  total = 'total',
}

export interface ModbusRegister {
  address: number
  registers: ModbusRegisterType
}

export enum ModbusRegisterType {
  holding = 'holding',
  input = 'input',
}

export enum ModbusDataType {
  INT32 = 'INT32',
  INT16 = 'INT16',
  FLOAT = 'FLOAT',
}

export enum WordOrder {
  LITTLE_ENDIAN = 'LITTLE_ENDIAN',
  BIG_ENDIAN = 'BIG_ENDIAN',
}

export interface LegacyModbusMetersConfig {
  // A computed property (that also may be modified during UI interactions)
  highestMeterNumber: number
}

export interface LegacyModbusSerialConfig {
  deviceNumber: number
  encoding: ModbusSerialEncoding
  transmissionDelayInMilliseconds?: number
}

export enum ModbusSerialEncoding {
  rtu = 'rtu',
  ascii = 'ascii',
  bin = 'bin',
}

/** Specific config of the legacy serial module */
export interface LegacySerialPortConfig {
  portConfig: SerialPortConfig
  serialPort: SerialPort
}

/** Generic config piece that can apply to any serial port using module */
export interface SerialPortConfig {
  baudRate: number
  dataBits: number
  parity: SerialPortParity
  stopBits: number
}

export enum SerialPortParity {
  none = 'none',
  even = 'even',
  odd = 'odd',
}

export interface TillquistConfig {
  powerEnabled: boolean
  frequencyEnabled: boolean
}
